import {
  type ISeriesPrimitive,
  type IPrimitivePaneView,
  type IPrimitivePaneRenderer,
  type SeriesAttachedParameter,
  type IChartApiBase,
  type ISeriesApi,
  type SeriesType,
  type Time,
} from "lightweight-charts"

// Duck-typed to avoid importing fancy-canvas directly (transitive dep only)
interface CanvasTarget {
  useBitmapCoordinateSpace<T>(
    f: (scope: {
      context: CanvasRenderingContext2D
      horizontalPixelRatio: number
      verticalPixelRatio: number
      bitmapSize: { width: number; height: number }
    }) => T,
  ): T
}

export interface Snapshot {
  time: number
  bids: Map<number, number>
  asks: Map<number, number>
}

export interface HeatmapParams {
  snapshots: Snapshot[]
  globalMaxVol: number
  precision: number
}

// Bookmap bi-color ramp: Bids = dark navy → cyan/teal, Asks = dark navy → orange/cream
const BID_STOPS: number[][] = [[6,10,16],[6,55,65],[20,140,135],[125,225,210],[235,255,250]]
const ASK_STOPS: number[][] = [[6,10,16],[60,18,18],[170,40,40],[245,140,90],[255,235,200]]

function rampStops(stops: number[][], t: number): [number, number, number] {
  const clamped = Math.max(0, Math.min(1, t))
  const seg = stops.length - 1
  const scaled = clamped * seg
  const lo = Math.min(Math.floor(scaled), seg - 1)
  const hi = lo + 1
  const u = scaled - lo
  const a = stops[lo]!
  const b = stops[hi]!
  return [
    Math.round(a[0]! + (b[0]! - a[0]!) * u),
    Math.round(a[1]! + (b[1]! - a[1]!) * u),
    Math.round(a[2]! + (b[2]! - a[2]!) * u),
  ]
}

function thermalColor(ratio: number, isBid: boolean): string {
  if (ratio < 0.03) return ""
  const t = Math.min(ratio, 1)
  const [r, g, b] = rampStops(isBid ? BID_STOPS : ASK_STOPS, t)
  const alpha = (0.4 + t * 0.55).toFixed(2)
  return `rgba(${r},${g},${b},${alpha})`
}

class HeatmapRenderer implements IPrimitivePaneRenderer {
  constructor(
    private _chart: IChartApiBase<Time>,
    private _series: ISeriesApi<SeriesType, Time>,
    private _p: HeatmapParams,
  ) {}

  draw(target: CanvasTarget): void {
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr, bitmapSize }) => {
      const { snapshots, globalMaxVol, precision } = this._p
      if (snapshots.length === 0 || globalMaxVol === 0) return
      const timeScale = this._chart.timeScale()
      // Cap cell width to 5% of canvas to prevent full-width stripes when zoomed in
      const maxCellW = Math.max(4, Math.round(bitmapSize.width * 0.05))

      // Compute cell height once from any pair of adjacent price levels
      let cellH = 4
      outer: for (const snap of snapshots) {
        for (const price of snap.bids.keys()) {
          const y0 = this._series.priceToCoordinate(price)
          const y1 = this._series.priceToCoordinate(price + precision)
          if (y0 !== null && y1 !== null && Math.abs(y0 - y1) > 0) {
            cellH = Math.max(2, Math.ceil(Math.abs(y0 - y1) * vpr))
            break outer
          }
        }
      }

      const logMax = Math.log1p(globalMaxVol)

      for (let si = 0; si < snapshots.length; si++) {
        const snap = snapshots[si]
        const xCoord = timeScale.timeToCoordinate(Math.floor(snap.time / 1000) as Time)
        if (xCoord === null) continue
        const xPx = Math.round(xCoord * hpr)

        // Cell width: distance to next snapshot (or mirror from previous)
        let cellW = 6
        const next = snapshots[si + 1]
        const prev = snapshots[si - 1]
        if (next !== undefined) {
          const xNext = timeScale.timeToCoordinate(Math.floor(next.time / 1000) as Time)
          if (xNext !== null) cellW = Math.min(maxCellW, Math.max(2, Math.ceil(Math.abs(xNext - xCoord) * hpr)))
        } else if (prev !== undefined) {
          const xPrev = timeScale.timeToCoordinate(Math.floor(prev.time / 1000) as Time)
          if (xPrev !== null) cellW = Math.min(maxCellW, Math.max(2, Math.ceil(Math.abs(xCoord - xPrev) * hpr)))
        }

        for (const [price, vol] of snap.bids) {
          const y = this._series.priceToCoordinate(price)
          if (y === null) continue
          const color = thermalColor(Math.log1p(vol) / logMax, true)
          if (!color) continue
          ctx.fillStyle = color
          ctx.fillRect(xPx, Math.round(y * vpr) - Math.floor(cellH / 2), cellW, cellH)
        }
        for (const [price, vol] of snap.asks) {
          const y = this._series.priceToCoordinate(price)
          if (y === null) continue
          const color = thermalColor(Math.log1p(vol) / logMax, false)
          if (!color) continue
          ctx.fillStyle = color
          ctx.fillRect(xPx, Math.round(y * vpr) - Math.floor(cellH / 2), cellW, cellH)
        }
      }
    })
  }
}

class HeatmapPaneView implements IPrimitivePaneView {
  constructor(
    private _chart: IChartApiBase<Time>,
    private _series: ISeriesApi<SeriesType, Time>,
    private _p: HeatmapParams,
  ) {}

  renderer(): IPrimitivePaneRenderer {
    return new HeatmapRenderer(this._chart, this._series, this._p)
  }
}

export class HeatmapPrimitive implements ISeriesPrimitive<Time> {
  private _p: HeatmapParams
  private _chart: IChartApiBase<Time> | null = null
  private _series: ISeriesApi<SeriesType, Time> | null = null
  private _requestUpdate: (() => void) | null = null

  constructor(p: HeatmapParams) {
    this._p = p
  }

  attached({ chart, series, requestUpdate }: SeriesAttachedParameter<Time>): void {
    this._chart = chart
    this._series = series
    this._requestUpdate = requestUpdate
  }

  detached(): void {
    this._chart = null
    this._series = null
    this._requestUpdate = null
  }

  updateParams(params: Partial<HeatmapParams>): void {
    this._p = { ...this._p, ...params }
    this._requestUpdate?.()
  }

  paneViews(): readonly IPrimitivePaneView[] {
    if (!this._chart || !this._series) return []
    return [new HeatmapPaneView(this._chart, this._series, this._p)]
  }
}
